// MARK://dhk8/pos/features/SharePublicLink
// Botón "Compartir enlace" para superficies públicas del comensal (fila/reservas)
// desde el POS. Abre un diálogo con: el enlace (con el slug del tenant de la
// sesión), copiar, código QR para escanear rápido, y compartir (share nativo +
// WhatsApp + correo). El slug se resuelve por el branding público by-tenant
// (devuelve `slug`), así no depende de que la sesión POS lo traiga.

import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { Button, Dialog } from '@dockethub/ui'
import { Share2, Copy, Check, QrCode as QrIcon } from 'lucide-react'
import { useTenantId } from '../hooks/useTenantId.js'
import { apiBase } from '../lib/api-origin.js'

interface Props {
  kind: 'fila' | 'reservar'
  label?: string
}

export function SharePublicLink({ kind, label = 'Compartir enlace' }: Props) {
  const tenantId = useTenantId()
  const [slug, setSlug] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!tenantId) return
    let cancelled = false
    fetch(`${apiBase()}/dhk8-platform-tenants/v1/public/branding/by-tenant/${tenantId}`, {
      headers: { accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((b: { data?: { slug?: string | null } } | null) => {
        if (!cancelled) setSlug(b?.data?.slug ?? null)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [tenantId])

  const url = slug
    ? kind === 'fila'
      ? `https://fila.nintaigo.com/fila/${slug}`
      : `https://reservar.nintaigo.com/reservar/${slug}`
    : null

  useEffect(() => {
    if (open && url) QRCode.toDataURL(url, { width: 240, margin: 1 }).then(setQr).catch(() => setQr(null))
  }, [open, url])

  const title = kind === 'fila' ? 'Enlace de la fila' : 'Enlace de reservas'
  const shareText = kind === 'fila'
    ? 'Anótate en la fila desde tu celular:'
    : 'Reserva tu mesa desde tu celular:'

  async function copy(): Promise<void> {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch { /* sin ruido */ }
  }
  async function nativeShare(): Promise<void> {
    if (!url) return
    try { if (navigator.share) await navigator.share({ title, text: shareText, url }) } catch { /* canceló */ }
  }

  const canNativeShare = typeof navigator !== 'undefined' && 'share' in navigator

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        leading={<Share2 size={14} />}
        onClick={() => setOpen(true)}
        disabled={!url}
        title={url ?? 'Cargando enlace…'}
      >
        {label}
      </Button>
      {open && (
        <Dialog
          open={true}
          onClose={() => setOpen(false)}
          title={title}
          footer={<Button variant="ghost" onClick={() => setOpen(false)}>Cerrar</Button>}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
            {qr ? (
              <img src={qr} alt="Código QR" width={220} height={220} style={{ background: '#fff', padding: 8 }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.6, height: 220 }}>
                <QrIcon size={18} /> Generando QR…
              </div>
            )}
            <code style={{ wordBreak: 'break-all', fontSize: 13, textAlign: 'center', opacity: 0.85 }}>{url}</code>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button variant="primary" size="sm" leading={copied ? <Check size={14} /> : <Copy size={14} />} onClick={() => void copy()}>
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
              {canNativeShare && (
                <Button variant="ghost" size="sm" leading={<Share2 size={14} />} onClick={() => void nativeShare()}>
                  Compartir…
                </Button>
              )}
              {url && (
                <Button variant="ghost" size="sm" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`, '_blank', 'noopener')}>
                  WhatsApp
                </Button>
              )}
              {url && (
                <Button variant="ghost" size="sm" onClick={() => window.open(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${shareText} ${url}`)}`)}>
                  Correo
                </Button>
              )}
            </div>
          </div>
        </Dialog>
      )}
    </>
  )
}
